import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { startGoogleAdsOAuth } from "@/lib/googleAdsOAuth";
import wordpressLogo from "@/assets/integrations/wordpress.svg";
import shopifyLogo from "@/assets/integrations/shopify.svg";
import wixLogo from "@/assets/integrations/wix.svg";
import webflowLogo from "@/assets/integrations/webflow.svg";
import squarespaceLogo from "@/assets/integrations/squarespace.svg";
import framerLogo from "@/assets/integrations/framer.svg";
import lovableLogo from "@/assets/integrations/lovable.png";
import googleLogo from "@/assets/integrations/google.svg";

interface Props {
  clientIds: string[];
}

type PlatformId = "lovable" | "wordpress" | "shopify" | "wix" | "webflow" | "squarespace" | "framer" | "google_analytics" | "google_ads";

const PLATFORMS: { id: PlatformId; name: string; logo: string }[] = [
  { id: "wordpress", name: "WordPress", logo: wordpressLogo },
  { id: "shopify", name: "Shopify", logo: shopifyLogo },
  { id: "wix", name: "Wix", logo: wixLogo },
  { id: "lovable", name: "Lovable", logo: lovableLogo },
  { id: "webflow", name: "Webflow", logo: webflowLogo },
  { id: "squarespace", name: "Squarespace", logo: squarespaceLogo },
  { id: "google_analytics", name: "Google Analytics", logo: googleLogo },
  { id: "google_ads", name: "Google Ads", logo: googleLogo },
  { id: "framer", name: "Framer", logo: framerLogo },
];

const COMING_SOON_FIELDS: Record<"framer", { label: string; placeholder: string; type?: string }[]> = {
  framer: [
    { label: "Framer Site URL", placeholder: "https://yoursite.framer.website" },
    { label: "Framer API Key", placeholder: "xxxxxxxxxxxx", type: "password" },
  ],
};

const RAILWAY_URL = (import.meta.env.VITE_RAILWAY_URL as string | undefined) || "https://seo-saas-cron-production.up.railway.app";

const IntegrationsTab = ({ clientIds }: Props) => {
  const clientId = clientIds[0];
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformId>("lovable");

  // Lovable state (Lovable-hosted sites read directly from Supabase — no creds needed)
  const [lvUrl, setLvUrl] = useState("");
  const [lvConnected, setLvConnected] = useState(false);
  const [lvConnectedUrl, setLvConnectedUrl] = useState<string | null>(null);
  const [lvSaving, setLvSaving] = useState(false);
  const [lvError, setLvError] = useState<string | null>(null);

  // WordPress state
  const [testing, setTesting] = useState(false);
  const [wpUrl, setWpUrl] = useState("");
  const [wpUsername, setWpUsername] = useState("");
  const [wpAppPassword, setWpAppPassword] = useState("");
  const [wpConnected, setWpConnected] = useState(false);
  const [wpConnectedUrl, setWpConnectedUrl] = useState<string | null>(null);
  const [wpError, setWpError] = useState<string | null>(null);

  // Shopify state
  const [shTesting, setShTesting] = useState(false);
  const [shStore, setShStore] = useState("");
  const [shToken, setShToken] = useState("");
  const [shConnected, setShConnected] = useState(false);
  const [shConnectedStore, setShConnectedStore] = useState<string | null>(null);
  const [shError, setShError] = useState<string | null>(null);

  // Wix state
  const [wixTesting, setWixTesting] = useState(false);
  const [wixSite, setWixSite] = useState("");
  const [wixKey, setWixKey] = useState("");
  const [wixConnected, setWixConnected] = useState(false);
  const [wixConnectedUrl, setWixConnectedUrl] = useState<string | null>(null);
  const [wixError, setWixError] = useState<string | null>(null);

  // Webflow state
  const [wfTesting, setWfTesting] = useState(false);
  const [wfSiteId, setWfSiteId] = useState("");
  const [wfToken, setWfToken] = useState("");
  const [wfConnected, setWfConnected] = useState(false);
  const [wfConnectedSite, setWfConnectedSite] = useState<string | null>(null);
  const [wfError, setWfError] = useState<string | null>(null);

  // Squarespace state
  const [sqTesting, setSqTesting] = useState(false);
  const [sqSite, setSqSite] = useState("");
  const [sqKey, setSqKey] = useState("");
  const [sqConnected, setSqConnected] = useState(false);
  const [sqConnectedUrl, setSqConnectedUrl] = useState<string | null>(null);
  const [sqError, setSqError] = useState<string | null>(null);

  // Google Analytics state
  const [gaConnected, setGaConnected] = useState(false);
  const [gaMeasurementId, setGaMeasurementId] = useState("");
  const [gaModalOpen, setGaModalOpen] = useState(false);
  const [gaInput, setGaInput] = useState("");
  const [gaSaving, setGaSaving] = useState(false);
  const [gaError, setGaError] = useState<string | null>(null);

  // Google Ads state
  type GAdsCust = { id: string; name: string; manager: boolean; currency: string };
  type GAdsChild = { id: string; name: string; managerId: string; currency: string };
  const [gAdsConnected, setGAdsConnected] = useState(false);
  const [gAdsConnecting, setGAdsConnecting] = useState(false);
  const [gAdsVerifying, setGAdsVerifying] = useState(false);
  const [gAdsEmail, setGAdsEmail] = useState<string | null>(null);
  const [gAdsManagers, setGAdsManagers] = useState<GAdsCust[]>([]);
  const [gAdsDirect, setGAdsDirect] = useState<GAdsCust[]>([]);
  const [gAdsChildren, setGAdsChildren] = useState<GAdsChild[]>([]);
  const [gAdsHasAdsAccount, setGAdsHasAdsAccount] = useState<boolean | null>(null);
  const [gAdsVerifyError, setGAdsVerifyError] = useState<string | null>(null);
  const [gAdsNotAdsUser, setGAdsNotAdsUser] = useState(false);
  const [gAdsSelectedCustomerId, setGAdsSelectedCustomerId] = useState<string | null>(null);
  const [gAdsSelectedLoginCustomerId, setGAdsSelectedLoginCustomerId] = useState<string | null>(null);
  const [gAdsSavingSelection, setGAdsSavingSelection] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("wp_url, wp_username, shopify_store_url, wix_site_url, webflow_site_id, squarespace_website_url, lovable_site_url, ga_measurement_id, ga_connected, google_ads_connected_at")
          .eq("id", clientId)
          .maybeSingle();
        if (error) {
          console.error("[IntegrationsTab] load error", error);
          setLoading(false);
          return;
        }

      if ((data as any)?.lovable_site_url) {
        setLvConnected(true);
        setLvConnectedUrl((data as any).lovable_site_url);
        setLvUrl((data as any).lovable_site_url);
      }

      // Connection state is derived from non-secret presence indicators only.
      // Tokens/passwords are stored server-side and never returned to the browser.
      if (data?.wp_url && data?.wp_username) {
        setWpConnected(true);
        setWpConnectedUrl(data.wp_url);
        setWpUrl(data.wp_url);
        setWpUsername(data.wp_username);
      }
      if ((data as any)?.shopify_store_url) {
        setShConnected(true);
        setShConnectedStore((data as any).shopify_store_url);
        setShStore((data as any).shopify_store_url);
      }
      if ((data as any)?.wix_site_url) {
        setWixConnected(true);
        setWixConnectedUrl((data as any).wix_site_url);
        setWixSite((data as any).wix_site_url);
      }
      if ((data as any)?.webflow_site_id) {
        setWfConnected(true);
        setWfConnectedSite((data as any).webflow_site_id);
        setWfSiteId((data as any).webflow_site_id);
      }
      if ((data as any)?.squarespace_website_url) {
        setSqConnected(true);
        setSqConnectedUrl((data as any).squarespace_website_url);
        setSqSite((data as any).squarespace_website_url);
      }
      if ((data as any)?.ga_connected && (data as any)?.ga_measurement_id) {
        setGaConnected(true);
        setGaMeasurementId((data as any).ga_measurement_id);
      }
      if ((data as any)?.google_ads_connected_at) {
        setGAdsConnected(true);
        // fire and forget verification
        setTimeout(() => { verifyGoogleAdsAccount(); }, 0);
      }
      } catch (e) {
        console.error("[IntegrationsTab] load exception", e);
      }
      setLoading(false);
    };
    load();
  }, [clientId]);

  const handleConnectLovable = async () => {
    setLvError(null);
    const url = lvUrl.trim();
    if (!url) {
      setLvError("Website URL is required.");
      return;
    }
    if (!clientId) {
      setLvError("No client found.");
      return;
    }
    setLvSaving(true);
    try {
      const normalized = url.replace(/\/+$/, "");
      const { error } = await supabase
        .from("clients")
        .update({ lovable_site_url: normalized } as any)
        .eq("id", clientId);
      if (error) throw error;
      setLvConnected(true);
      setLvConnectedUrl(normalized);
      toast({
        title: "✅ Lovable site connected",
        description: "Articles will appear automatically on your Lovable site's blog.",
      });
    } catch (e: any) {
      setLvError(e?.message || "Failed to save Lovable site.");
    } finally {
      setLvSaving(false);
    }
  };

  const handleDisconnectLovable = async () => {
    if (!clientId) return;
    const { error } = await supabase
      .from("clients")
      .update({ lovable_site_url: null } as any)
      .eq("id", clientId);
    if (error) {
      setLvError(error.message);
      return;
    }
    setLvConnected(false);
    setLvConnectedUrl(null);
    setLvUrl("");
    toast({ title: "Lovable site disconnected" });
  };

  const handleConnectWordPress = async () => {
    setWpError(null);
    if (!wpUrl.trim() || !wpUsername.trim() || !wpAppPassword.trim()) {
      setWpError("All fields are required.");
      return;
    }
    if (!clientId) {
      setWpError("No client found.");
      return;
    }
    setTesting(true);
    try {
      const res = await fetch(`${RAILWAY_URL.replace(/\/$/, "")}/test-wordpress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          wp_url: wpUrl.trim(),
          wp_username: wpUsername.trim(),
          wp_app_password: wpAppPassword.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
      }
      const { error: updateError } = await supabase
        .from("clients")
        .update({
          wp_url: wpUrl.trim(),
          wp_username: wpUsername.trim(),
          wp_app_password: wpAppPassword.trim(),
        })
        .eq("id", clientId);
      if (updateError) throw updateError;

      setWpConnected(true);
      setWpConnectedUrl(wpUrl.trim());
      toast({ title: "✅ WordPress Connected" });
    } catch (e: any) {
      setWpError(e?.message || "Failed to connect to WordPress.");
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnectWordPress = async () => {
    if (!clientId) return;
    const { error: updateError } = await supabase
      .from("clients")
      .update({ wp_url: null, wp_username: null, wp_app_password: null })
      .eq("id", clientId);
    if (updateError) {
      setWpError(updateError.message);
      return;
    }
    setWpConnected(false);
    setWpConnectedUrl(null);
    setWpUrl("");
    setWpUsername("");
    setWpAppPassword("");
    toast({ title: "WordPress disconnected" });
  };

  const handleConnectShopify = async () => {
    setShError(null);
    if (!shStore.trim() || !shToken.trim()) {
      setShError("Both fields are required.");
      return;
    }
    if (!clientId) {
      setShError("No client found.");
      return;
    }
    setShTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("test-shopify", {
        body: { store_url: shStore.trim(), access_token: shToken.trim() },
      });
      if (error) throw new Error(error.message);
      if (!data?.success) {
        throw new Error(data?.error || "Could not connect to Shopify.");
      }

      const normalizedStore = data.store as string;
      const blogId = (data.blog_id as string | null) ?? null;

      const { error: updateError } = await supabase
        .from("clients")
        .update({
          shopify_store_url: normalizedStore,
          shopify_access_token: shToken.trim(),
          shopify_blog_id: blogId,
        } as any)
        .eq("id", clientId);
      if (updateError) throw updateError;

      setShConnected(true);
      setShConnectedStore(normalizedStore);
      toast({
        title: "✅ Shopify Connected",
        description: blogId ? "Articles will publish to your store blog." : "Connected — create a blog in Shopify to start publishing.",
      });
    } catch (e: any) {
      setShError(e?.message || "Failed to connect to Shopify.");
    } finally {
      setShTesting(false);
    }
  };

  const handleDisconnectShopify = async () => {
    if (!clientId) return;
    const { error: updateError } = await supabase
      .from("clients")
      .update({
        shopify_store_url: null,
        shopify_access_token: null,
        shopify_blog_id: null,
      } as any)
      .eq("id", clientId);
    if (updateError) {
      setShError(updateError.message);
      return;
    }
    setShConnected(false);
    setShConnectedStore(null);
    setShStore("");
    setShToken("");
    toast({ title: "Shopify disconnected" });
  };

  const handleConnectWix = async () => {
    setWixError(null);
    if (!wixSite.trim() || !wixKey.trim()) {
      setWixError("Both fields are required.");
      return;
    }
    if (!clientId) {
      setWixError("No client found.");
      return;
    }
    setWixTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("test-wix", {
        body: { site_url: wixSite.trim(), api_key: wixKey.trim() },
      });
      if (error) throw new Error(error.message);
      if (!data?.success) {
        throw new Error(data?.error || "Could not connect to Wix.");
      }

      const site = (data.site as string) || wixSite.trim();
      const { error: updateError } = await supabase
        .from("clients")
        .update({
          wix_site_url: site,
          wix_api_key: wixKey.trim(),
        } as any)
        .eq("id", clientId);
      if (updateError) throw updateError;

      setWixConnected(true);
      setWixConnectedUrl(site);
      toast({ title: "✅ Wix Connected", description: "Articles will publish to your Wix blog." });
    } catch (e: any) {
      setWixError(e?.message || "Failed to connect to Wix.");
    } finally {
      setWixTesting(false);
    }
  };

  const handleDisconnectWix = async () => {
    if (!clientId) return;
    const { error: updateError } = await supabase
      .from("clients")
      .update({ wix_site_url: null, wix_api_key: null } as any)
      .eq("id", clientId);
    if (updateError) {
      setWixError(updateError.message);
      return;
    }
    setWixConnected(false);
    setWixConnectedUrl(null);
    setWixSite("");
    setWixKey("");
    toast({ title: "Wix disconnected" });
  };

  const handleConnectWebflow = async () => {
    setWfError(null);
    if (!wfSiteId.trim() || !wfToken.trim()) {
      setWfError("Both fields are required.");
      return;
    }
    if (!clientId) {
      setWfError("No client found.");
      return;
    }
    setWfTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("test-webflow", {
        body: { site_id: wfSiteId.trim(), api_token: wfToken.trim() },
      });
      if (error) throw new Error(error.message);
      if (!data?.success) {
        throw new Error(data?.error || "Could not connect to Webflow.");
      }

      const collectionId = (data.collection_id as string | null) ?? null;
      const { error: updateError } = await supabase
        .from("clients")
        .update({
          webflow_site_id: wfSiteId.trim(),
          webflow_api_token: wfToken.trim(),
          webflow_collection_id: collectionId,
        } as any)
        .eq("id", clientId);
      if (updateError) throw updateError;

      setWfConnected(true);
      setWfConnectedSite(wfSiteId.trim());
      toast({
        title: "✅ Webflow Connected",
        description: collectionId ? "Articles will publish to your Webflow collection." : "Connected — create a blog collection in Webflow to start publishing.",
      });
    } catch (e: any) {
      setWfError(e?.message || "Failed to connect to Webflow.");
    } finally {
      setWfTesting(false);
    }
  };

  const handleDisconnectWebflow = async () => {
    if (!clientId) return;
    const { error: updateError } = await supabase
      .from("clients")
      .update({ webflow_site_id: null, webflow_api_token: null, webflow_collection_id: null } as any)
      .eq("id", clientId);
    if (updateError) {
      setWfError(updateError.message);
      return;
    }
    setWfConnected(false);
    setWfConnectedSite(null);
    setWfSiteId("");
    setWfToken("");
    toast({ title: "Webflow disconnected" });
  };

  const handleConnectSquarespace = async () => {
    setSqError(null);
    if (!sqSite.trim() || !sqKey.trim()) {
      setSqError("Both fields are required.");
      return;
    }
    if (!clientId) {
      setSqError("No client found.");
      return;
    }
    setSqTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("test-squarespace", {
        body: { website_url: sqSite.trim(), api_key: sqKey.trim() },
      });
      if (error) throw new Error(error.message);
      if (!data?.success) {
        throw new Error(data?.error || "Could not connect to Squarespace.");
      }

      const { error: updateError } = await supabase
        .from("clients")
        .update({
          squarespace_website_url: sqSite.trim(),
          squarespace_api_key: sqKey.trim(),
        } as any)
        .eq("id", clientId);
      if (updateError) throw updateError;

      setSqConnected(true);
      setSqConnectedUrl(sqSite.trim());
      toast({ title: "✅ Squarespace Connected", description: "Articles will publish to your Squarespace blog." });
    } catch (e: any) {
      setSqError(e?.message || "Failed to connect to Squarespace.");
    } finally {
      setSqTesting(false);
    }
  };

  const handleDisconnectSquarespace = async () => {
    if (!clientId) return;
    const { error: updateError } = await supabase
      .from("clients")
      .update({ squarespace_website_url: null, squarespace_api_key: null } as any)
      .eq("id", clientId);
    if (updateError) {
      setSqError(updateError.message);
      return;
    }
    setSqConnected(false);
    setSqConnectedUrl(null);
    setSqSite("");
    setSqKey("");
    toast({ title: "Squarespace disconnected" });
  };

  // Google Analytics handlers
  const openGaModal = () => {
    setGaInput(gaMeasurementId || "");
    setGaError(null);
    setGaModalOpen(true);
  };

  const handleSaveGa = async () => {
    setGaError(null);
    const id = gaInput.trim();
    if (!/^G-[A-Z0-9]{6,}$/i.test(id)) {
      setGaError("Enter a valid GA4 Measurement ID (e.g. G-XXXXXXXXXX).");
      return;
    }
    if (!clientId) {
      setGaError("No client found.");
      return;
    }
    setGaSaving(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update({ ga_measurement_id: id, ga_connected: true } as any)
        .eq("id", clientId);
      if (error) throw error;
      setGaConnected(true);
      setGaMeasurementId(id);
      setGaModalOpen(false);
      toast({ title: "✅ Google Analytics connected", description: id });
    } catch (e: any) {
      setGaError(e?.message || "Failed to save Measurement ID.");
    } finally {
      setGaSaving(false);
    }
  };

  const handleDisconnectGa = async () => {
    if (!clientId) return;
    const { error } = await supabase
      .from("clients")
      .update({ ga_measurement_id: null, ga_connected: false } as any)
      .eq("id", clientId);
    if (error) {
      toast({ title: "Error", description: error.message });
      return;
    }
    setGaConnected(false);
    setGaMeasurementId("");
    toast({ title: "Google Analytics disconnected" });
  };

  const handleConnectGoogleAds = async () => {
    setGAdsConnecting(true);
    const redirectUri = `${window.location.origin}/google-ads-callback`;
    toast({
      title: "Opening Google authentication…",
      description: `redirect_uri = ${redirectUri} — this exact string must be in Google Cloud → OAuth Client → Authorized redirect URIs.`,
    });
    try {
      await startGoogleAdsOAuth();
    } catch (e: any) {
      setGAdsConnecting(false);
      const msg = e?.message || "Could not start the OAuth flow.";
      const isMismatch = /redirect_uri_mismatch/i.test(msg);
      toast({
        title: isMismatch ? "redirect_uri_mismatch" : "Google Ads connection failed",
        description: isMismatch
          ? `Google rejected: ${redirectUri}. Add this EXACT URL (no trailing slash) to Google Cloud → APIs & Services → Credentials → your OAuth 2.0 Client → Authorized redirect URIs, save, wait ~1 min, then retry.`
          : msg,
        variant: "destructive",
      });
    }
  };


  const verifyGoogleAdsAccount = async () => {
    if (!clientId) return;
    setGAdsVerifying(true);
    setGAdsVerifyError(null);
    setGAdsNotAdsUser(false);
    try {
      const { data, error } = await supabase.functions.invoke("verify-google-ads-account", {
        body: { clientId },
      });
      if (error) throw error;
      const payload: any = data;
      if (!payload?.ok) {
        setGAdsVerifyError(payload?.error || "Verification failed");
        return;
      }
      setGAdsEmail(payload.email || null);
      setGAdsManagers(payload.managerAccounts || []);
      setGAdsDirect(payload.directAccounts || []);
      setGAdsChildren(payload.childAccounts || []);
      setGAdsHasAdsAccount(!!payload.hasAdsAccount);
      setGAdsNotAdsUser(!!payload.notAdsUser);
      const sel = payload.selected || {};
      setGAdsSelectedCustomerId(sel.customerId || null);
      setGAdsSelectedLoginCustomerId(sel.loginCustomerId || null);
      if (payload.error) setGAdsVerifyError(payload.error);
    } catch (e: any) {
      setGAdsVerifyError(e?.message || "Verification failed");
    } finally {
      setGAdsVerifying(false);
    }
  };

  const saveGoogleAdsSelection = async (customerId: string, loginCustomerId: string | null) => {
    if (!clientId) return;
    setGAdsSavingSelection(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update({
          google_ads_customer_id: customerId,
          google_ads_login_customer_id: loginCustomerId,
        } as any)
        .eq("id", clientId);
      if (error) throw error;
      setGAdsSelectedCustomerId(customerId);
      setGAdsSelectedLoginCustomerId(loginCustomerId);
      toast({ title: "Publishing target saved", description: `Customer ID: ${customerId}` });
    } catch (e: any) {
      toast({ title: "Could not save selection", description: e?.message || "Unknown error" });
    } finally {
      setGAdsSavingSelection(false);
    }
  };

  const handleDisconnectGoogleAds = async () => {
    if (!clientId) return;
    const { error } = await supabase
      .from("clients")
      .update({
        google_ads_access_token: null,
        google_ads_refresh_token: null,
        google_ads_connected_at: null,
        google_ads_customer_id: null,
        google_ads_login_customer_id: null,
      } as any)
      .eq("id", clientId);
    if (error) {
      toast({ title: "Error", description: error.message });
      return;
    }
    setGAdsConnected(false);
    setGAdsEmail(null);
    setGAdsManagers([]);
    setGAdsDirect([]);
    setGAdsChildren([]);
    setGAdsHasAdsAccount(null);
    setGAdsNotAdsUser(false);
    setGAdsVerifyError(null);
    setGAdsSelectedCustomerId(null);
    setGAdsSelectedLoginCustomerId(null);
    toast({ title: "Google Ads disconnected" });
  };

  if (loading) {
    return <div className="text-muted-foreground">Loading…</div>;
  }

  const isLive = (id: PlatformId) => id === "lovable" || id === "wordpress" || id === "shopify" || id === "wix" || id === "webflow" || id === "squarespace" || id === "google_analytics" || id === "google_ads";

  return (
    <div className="max-w-[640px] text-foreground">
      <h1 className="mb-2 text-[26px] font-[800] tracking-[-0.02em]">
        Integrations
      </h1>
      <p className="mb-6 text-[14px] text-muted-foreground">
        Connect external services to publish and sync content automatically.
      </p>

      <div className="mb-6 flex flex-wrap gap-3">
        {PLATFORMS.map((p) => {
          const isSelected = selectedPlatform === p.id;
          const connected = (p.id === "lovable" && lvConnected) || (p.id === "wordpress" && wpConnected) || (p.id === "shopify" && shConnected) || (p.id === "wix" && wixConnected) || (p.id === "webflow" && wfConnected) || (p.id === "squarespace" && sqConnected) || (p.id === "google_analytics" && gaConnected) || (p.id === "google_ads" && gAdsConnected);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedPlatform(p.id)}
              className={`relative flex min-w-[110px] flex-col items-center gap-2 rounded-[14px] border-2 bg-card px-4 py-3 transition-all ${
                isSelected
                  ? "border-[#22c55e] shadow-[0_4px_18px_rgba(34,197,94,0.18)]"
                  : "border-border hover:border-foreground/20"
              }`}
            >
              <img src={p.logo} alt={p.name} className="h-8 w-8 object-contain" />
              <span className="text-[12.5px] font-[700] text-foreground">{p.name}</span>
              {connected && (
                <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[#22c55e] ring-2 ring-background" />
              )}
            </button>
          );
        })}
      </div>

      {selectedPlatform === "lovable" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect Lovable site
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                For sites built and hosted on Lovable. No API keys needed — articles render automatically from your OrbitRank database.
              </div>
            </div>
            {lvConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {lvConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ Lovable site connected</span>
                <div className="mt-1 break-all text-muted-foreground">{lvConnectedUrl}</div>
              </div>
              <button
                onClick={handleDisconnectLovable}
                className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field
                label="Lovable Site URL"
                value={lvUrl}
                onChange={setLvUrl}
                placeholder="https://yoursite.lovable.app"
              />
              {lvError && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                  {lvError}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleConnectLovable}
                  disabled={lvSaving}
                  className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
                >
                  {lvSaving ? "Saving…" : "Connect"}
                </button>
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                Works with any site published on Lovable (`.lovable.app`) or a custom domain pointing to a Lovable project. The blog page on your site reads articles directly from OrbitRank — no setup needed beyond pasting your URL.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "wordpress" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect WordPress
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                Auto-publish generated content to your WordPress site.
              </div>
            </div>
            {wpConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {wpConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ WordPress Connected</span>
                <div className="mt-1 break-all text-muted-foreground">
                  {wpConnectedUrl}
                </div>
              </div>
              <button
                onClick={handleDisconnectWordPress}
                className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field label="WordPress Site URL" value={wpUrl} onChange={setWpUrl} placeholder="https://yoursite.com" />
              <Field label="WordPress Username" value={wpUsername} onChange={setWpUsername} placeholder="admin" />
              <Field label="WordPress Application Password" value={wpAppPassword} onChange={setWpAppPassword} placeholder="xxxx xxxx xxxx xxxx" type="password" />
              {wpError && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                  {wpError}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleConnectWordPress}
                  disabled={testing}
                  className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
                >
                  {testing ? "Testing…" : "Test & Connect"}
                </button>
                <button
                  onClick={() => {
                    setWpError(null);
                    setWpConnected(true);
                    setWpConnectedUrl("https://demo-site.example.com");
                    toast({ title: "✅ Demo WordPress Connected", description: "Simulated connection — no real site contacted." });
                  }}
                  className="self-start rounded-[10px] border border-border bg-secondary px-4 py-[10px] text-[12.5px] font-[700] uppercase tracking-wider text-secondary-foreground transition-all hover:bg-secondary/80"
                >
                  Test with demo site
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "shopify" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect Shopify
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                Auto-publish generated articles to your Shopify store blog.
              </div>
            </div>
            {shConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {shConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ Shopify Connected</span>
                <div className="mt-1 break-all text-muted-foreground">
                  {shConnectedStore}
                </div>
              </div>
              <button
                onClick={handleDisconnectShopify}
                className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field
                label="Shopify Store URL"
                value={shStore}
                onChange={setShStore}
                placeholder="yourstore.myshopify.com"
              />
              <Field
                label="Admin API Access Token"
                value={shToken}
                onChange={setShToken}
                placeholder="shpat_xxxxxxxxxxxx"
                type="password"
              />
              {shError && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                  {shError}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleConnectShopify}
                  disabled={shTesting}
                  className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
                >
                  {shTesting ? "Testing…" : "Test & Connect"}
                </button>
                <button
                  onClick={() => {
                    setShError(null);
                    setShConnected(true);
                    setShConnectedStore("demo-store.myshopify.com");
                    toast({ title: "✅ Demo Shopify Connected", description: "Simulated connection — no real store contacted." });
                  }}
                  className="self-start rounded-[10px] border border-border bg-secondary px-4 py-[10px] text-[12.5px] font-[700] uppercase tracking-wider text-secondary-foreground transition-all hover:bg-secondary/80"
                >
                  Test with demo store
                </button>
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                Create a custom app in Shopify Admin → Settings → Apps and sales channels → Develop apps. Grant the <span className="font-mono">write_content</span> scope and use the Admin API access token here.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "wix" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect Wix
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                Auto-publish generated articles to your Wix blog.
              </div>
            </div>
            {wixConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {wixConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ Wix Connected</span>
                <div className="mt-1 break-all text-muted-foreground">
                  {wixConnectedUrl}
                </div>
              </div>
              <button
                onClick={handleDisconnectWix}
                className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field
                label="Wix Site URL"
                value={wixSite}
                onChange={setWixSite}
                placeholder="https://yoursite.wixsite.com"
              />
              <Field
                label="Wix API Key"
                value={wixKey}
                onChange={setWixKey}
                placeholder="xxxx-xxxx-xxxx"
                type="password"
              />
              {wixError && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                  {wixError}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleConnectWix}
                  disabled={wixTesting}
                  className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
                >
                  {wixTesting ? "Testing…" : "Test & Connect"}
                </button>
                <button
                  onClick={() => {
                    setWixError(null);
                    setWixConnected(true);
                    setWixConnectedUrl("https://demo-site.wixsite.com");
                    toast({ title: "✅ Demo Wix Connected", description: "Simulated connection — no real site contacted." });
                  }}
                  className="self-start rounded-[10px] border border-border bg-secondary px-4 py-[10px] text-[12.5px] font-[700] uppercase tracking-wider text-secondary-foreground transition-all hover:bg-secondary/80"
                >
                  Test with demo site
                </button>
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                Generate an API key in your Wix dashboard under Settings → Headless Settings → API Keys. Grant the <span className="font-mono">Wix Blog</span> scope and paste the key here.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "webflow" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect Webflow
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                Auto-publish generated articles to your Webflow CMS collection.
              </div>
            </div>
            {wfConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {wfConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ Webflow Connected</span>
                <div className="mt-1 break-all text-muted-foreground">
                  Site ID: {wfConnectedSite}
                </div>
              </div>
              <button
                onClick={handleDisconnectWebflow}
                className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field
                label="Webflow Site ID"
                value={wfSiteId}
                onChange={setWfSiteId}
                placeholder="65f1a2b3c4d5e6f7a8b9c0d1"
              />
              <Field
                label="Webflow API Token"
                value={wfToken}
                onChange={setWfToken}
                placeholder="xxxxxxxxxxxx"
                type="password"
              />
              {wfError && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                  {wfError}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleConnectWebflow}
                  disabled={wfTesting}
                  className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
                >
                  {wfTesting ? "Testing…" : "Test & Connect"}
                </button>
                <button
                  onClick={() => {
                    setWfError(null);
                    setWfConnected(true);
                    setWfConnectedSite("demo-site-id");
                    toast({ title: "✅ Demo Webflow Connected", description: "Simulated connection — no real site contacted." });
                  }}
                  className="self-start rounded-[10px] border border-border bg-secondary px-4 py-[10px] text-[12.5px] font-[700] uppercase tracking-wider text-secondary-foreground transition-all hover:bg-secondary/80"
                >
                  Test with demo site
                </button>
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                Find your Site ID in Webflow → Site settings → General. Generate an API token under Site settings → Apps & Integrations → API access with CMS read & write scopes.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "squarespace" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect Squarespace
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                Auto-publish generated articles to your Squarespace blog.
              </div>
            </div>
            {sqConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {sqConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ Squarespace Connected</span>
                <div className="mt-1 break-all text-muted-foreground">
                  {sqConnectedUrl}
                </div>
              </div>
              <button
                onClick={handleDisconnectSquarespace}
                className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field
                label="Squarespace Website URL"
                value={sqSite}
                onChange={setSqSite}
                placeholder="https://yoursite.squarespace.com"
              />
              <Field
                label="Squarespace API Key"
                value={sqKey}
                onChange={setSqKey}
                placeholder="xxxxxxxxxxxx"
                type="password"
              />
              {sqError && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                  {sqError}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleConnectSquarespace}
                  disabled={sqTesting}
                  className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
                >
                  {sqTesting ? "Testing…" : "Test & Connect"}
                </button>
                <button
                  onClick={() => {
                    setSqError(null);
                    setSqConnected(true);
                    setSqConnectedUrl("https://demo-site.squarespace.com");
                    toast({ title: "✅ Demo Squarespace Connected", description: "Simulated connection — no real site contacted." });
                  }}
                  className="self-start rounded-[10px] border border-border bg-secondary px-4 py-[10px] text-[12.5px] font-[700] uppercase tracking-wider text-secondary-foreground transition-all hover:bg-secondary/80"
                >
                  Test with demo site
                </button>
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                Generate an API key in your Squarespace dashboard under Settings → Advanced → Developer API Keys with Blog read & write scopes.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "google_analytics" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src={googleLogo} alt="Google Analytics" className="h-9 w-9 object-contain" />
              <div>
                <div className="text-[16px] font-[700] text-foreground">
                  Google Analytics
                </div>
                <div className="mt-1 text-[12.5px] text-muted-foreground">
                  Connect your GA4 property to see your traffic data inside the dashboard.
                </div>
              </div>
            </div>
            {gaConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {gaConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground">
                <span className="text-primary">✅ Google Analytics connected</span>
                <div className="mt-1 break-all font-mono text-muted-foreground">{gaMeasurementId}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={openGaModal}
                  className="self-start rounded-[10px] border border-border bg-secondary px-4 py-2 text-[13px] font-[700] text-secondary-foreground transition-all"
                >
                  Update ID
                </button>
                <button
                  onClick={handleDisconnectGa}
                  className="self-start rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
                >
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-[13px] text-muted-foreground">
                Paste your GA4 Measurement ID to connect. You can find it in Google Analytics → Admin → Data Streams.
              </p>
              <button
                onClick={openGaModal}
                className="self-start rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90"
              >
                Connect Google Analytics
              </button>
            </div>
          )}
        </div>
      )}

      {selectedPlatform === "google_ads" && (
        <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[16px] font-[700] text-foreground">
                Connect Google Ads
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                Connect your Google Ads account to unlock AI-powered campaign management.
              </div>
            </div>
            {gAdsConnected && (
              <div className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-[700] text-primary">
                ✅ Connected
              </div>
            )}
          </div>

          {gAdsConnected ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-primary/20 bg-primary/5 p-4 text-[13px] text-foreground space-y-2">
                <div className="text-primary font-[700]">✅ OAuth token stored</div>
                <div className="text-muted-foreground">
                  <span className="font-[600] text-foreground">Connected Google account:</span>{" "}
                  {gAdsVerifying ? "Checking…" : (gAdsEmail || "—")}
                </div>
                {gAdsManagers.length > 0 && (
                  <div className="text-muted-foreground">
                    <span className="font-[600] text-foreground">Manager account{gAdsManagers.length > 1 ? "s" : ""} (MCC):</span>{" "}
                    {gAdsManagers.map((m) => `${m.name} (${m.id})`).join(", ")}
                  </div>
                )}
                <div className="text-muted-foreground">
                  <span className="font-[600] text-foreground">Publishing target:</span>{" "}
                  {gAdsSelectedCustomerId
                    ? `${gAdsSelectedCustomerId}${gAdsSelectedLoginCustomerId ? ` (via MCC ${gAdsSelectedLoginCustomerId})` : ""}`
                    : <span className="text-destructive">Not selected</span>}
                </div>
              </div>

              {!gAdsVerifying && (gAdsChildren.length > 0 || gAdsDirect.length > 0) && (
                <div className="rounded-[10px] border border-border bg-card p-4 text-[13px] space-y-2">
                  <div className="font-[700] text-foreground">Choose publishing account</div>
                  <div className="text-muted-foreground text-[12px]">
                    Campaigns will be created inside the selected child account. MCC accounts cannot host campaigns.
                  </div>
                  <div className="flex flex-col gap-2 pt-1">
                    {gAdsDirect.map((c) => {
                      const selected = gAdsSelectedCustomerId === c.id && !gAdsSelectedLoginCustomerId;
                      return (
                        <button
                          key={`direct-${c.id}`}
                          disabled={gAdsSavingSelection}
                          onClick={() => saveGoogleAdsSelection(c.id, null)}
                          className={`text-left rounded-[8px] border px-3 py-2 transition-all ${
                            selected ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                          }`}
                        >
                          <div className="font-[700] text-foreground">{c.name}</div>
                          <div className="text-[12px] text-muted-foreground">Direct account · {c.id}{c.currency ? ` · ${c.currency}` : ""}</div>
                        </button>
                      );
                    })}
                    {gAdsChildren.map((c) => {
                      const selected = gAdsSelectedCustomerId === c.id && gAdsSelectedLoginCustomerId === c.managerId;
                      return (
                        <button
                          key={`child-${c.managerId}-${c.id}`}
                          disabled={gAdsSavingSelection}
                          onClick={() => saveGoogleAdsSelection(c.id, c.managerId)}
                          className={`text-left rounded-[8px] border px-3 py-2 transition-all ${
                            selected ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                          }`}
                        >
                          <div className="font-[700] text-foreground">{c.name}</div>
                          <div className="text-[12px] text-muted-foreground">Child of MCC {c.managerId} · {c.id}{c.currency ? ` · ${c.currency}` : ""}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}


              {gAdsHasAdsAccount === false && (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-4 text-[13px] text-destructive">
                  <div className="font-[700] mb-1">
                    {gAdsNotAdsUser ? "NOT_ADS_USER" : "No Google Ads account"}
                  </div>
                  <div>
                    This Google account is not associated with any Google Ads account.
                    Please reconnect using the correct Google account.
                  </div>
                </div>
              )}

              {gAdsVerifyError && gAdsHasAdsAccount !== false && (
                <div className="rounded-[10px] border border-yellow-500/30 bg-yellow-500/10 p-4 text-[13px] text-foreground">
                  {gAdsVerifyError}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={verifyGoogleAdsAccount}
                  disabled={gAdsVerifying}
                  className="rounded-[10px] border border-border bg-card px-4 py-2 text-[13px] font-[700] text-foreground transition-all hover:bg-muted disabled:opacity-60"
                >
                  {gAdsVerifying ? "Verifying…" : "Re-verify"}
                </button>
                <button
                  onClick={async () => { await handleDisconnectGoogleAds(); await handleConnectGoogleAds(); }}
                  className="rounded-[10px] bg-[#4285F4] px-4 py-2 text-[13px] font-[700] text-white transition-all hover:opacity-90"
                >
                  Reconnect with different account
                </button>
                <button
                  onClick={handleDisconnectGoogleAds}
                  className="rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-2 text-[13px] font-[700] text-destructive transition-all"
                >
                  Disconnect
                </button>
              </div>
            </div>

          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-[13px] text-muted-foreground">
                You'll be redirected to Google to authorize OrbitRank access to your Google Ads account.
              </p>
              <button
                onClick={handleConnectGoogleAds}
                disabled={gAdsConnecting}
                className="self-start rounded-[10px] bg-[#4285F4] px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-white transition-all hover:opacity-90 disabled:opacity-60"
              >
                {gAdsConnecting ? "Redirecting…" : "Connect Google Ads Account"}
              </button>
            </div>
          )}
        </div>
      )}

      {gaModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !gaSaving && setGaModalOpen(false)}
        >
          <div
            className="w-full max-w-[420px] rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.2)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center gap-3">
              <img src={googleLogo} alt="" className="h-8 w-8 object-contain" />
              <div className="text-[16px] font-[700] text-foreground">Connect Google Analytics</div>
            </div>
            <p className="mb-4 text-[12.5px] text-muted-foreground">
              Enter your GA4 Measurement ID. It starts with <span className="font-mono">G-</span> followed by letters and numbers.
            </p>
            <Field
              label="GA4 Measurement ID"
              value={gaInput}
              onChange={setGaInput}
              placeholder="G-XXXXXXXXXX"
            />
            {gaError && (
              <div className="mt-3 rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-[12.5px] text-destructive">
                {gaError}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setGaModalOpen(false)}
                disabled={gaSaving}
                className="rounded-[10px] border border-border bg-secondary px-4 py-2 text-[13px] font-[700] text-secondary-foreground disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGa}
                disabled={gaSaving}
                className="rounded-[10px] bg-primary px-5 py-2 text-[13px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
              >
                {gaSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLive(selectedPlatform) && (
        <ComingSoonForm platform={PLATFORMS.find((p) => p.id === selectedPlatform)!} />
      )}
    </div>
  );
};

const ComingSoonForm = ({ platform }: { platform: { id: PlatformId; name: string; logo: string } }) => {
  const fields = COMING_SOON_FIELDS[platform.id as "framer"] ?? [];
  return (
    <div className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <div className="text-[16px] font-[700] text-foreground">
            Connect {platform.name}
          </div>
          <div className="mt-1 text-[12.5px] text-muted-foreground">
            Auto-publish generated content to your {platform.name} site.
          </div>
        </div>
        <div className="rounded-full border border-border bg-secondary px-3 py-1 text-[11px] font-[700] uppercase tracking-wider text-muted-foreground">
          Coming Soon
        </div>
      </div>
      <div className="flex flex-col gap-3 opacity-60">
        {fields.map((f) => (
          <label key={f.label} className="flex flex-col gap-[6px]">
            <span className="text-[11.5px] font-[700] uppercase tracking-[1px] text-muted-foreground">
              {f.label}
            </span>
            <input
              type={f.type || "text"}
              disabled
              placeholder={f.placeholder}
              className="h-11 cursor-not-allowed rounded-[10px] border border-input bg-background px-3 text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>
        ))}
        <button
          disabled
          className="mt-2 self-start cursor-not-allowed rounded-[10px] bg-primary px-5 py-[10px] text-[13px] font-[800] uppercase tracking-wider text-primary-foreground"
        >
          Coming Soon
        </button>
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) => (
  <label className="flex flex-col gap-[6px]">
    <span className="text-[11.5px] font-[700] uppercase tracking-[1px] text-muted-foreground">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-11 rounded-[10px] border border-input bg-background px-3 text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
    />
  </label>
);

export default IntegrationsTab;
